/**
 * PPTX-specific highlighting utilities
 * Adapts the DOCX highlighter for PowerPoint slides with proper handling of
 * CSS transforms, scaling, and slide-specific content structure.
 */

import { triggerHighlight } from "../DocxViewer/docxHighlighter";
import { clearProductTimeout, setProductTimeout } from "@/utils/productTimers";

/**
 * How long to wait for a smooth scroll's `scrollend` before running anyway:
 * no `scrollend` fires when the scroll had nothing to do (the target was
 * already in place) or when the browser lacks the event.
 */
const SCROLL_SETTLE_FALLBACK_MS = 500;

/**
 * Runs `after` once, when the smooth scroll just started settles: the first
 * `scrollend` from any scroller (captured at the window, since `scrollend`
 * does not bubble and the scroller may be `.pptx-viewer`, an ancestor, or the
 * document), or the fallback wait when none comes (#12187).
 */
function afterScrollSettles(after: () => void): void {
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    window.removeEventListener("scrollend", finish, true);
    clearProductTimeout(fallback);
    after();
  };
  window.addEventListener("scrollend", finish, true);
  const fallback = setProductTimeout(finish, SCROLL_SETTLE_FALLBACK_MS);
}

/**
 * Attempts to highlight the specified phrase inside a PPTX slide container.
 * @param slideNumber The slide number to focus the search on.
 * @param paragraph Paragraph content to highlight.
 * @param snippet If specified, highlights only that exact match inside the paragraph (usually a numeric figure).
 */
export function highlightPptx(
  slideNumber: number,
  paragraph: string,
  snippet?: string,
  // The snippet where the slide prints it, or nothing (#12090): a table
  // cell's paragraph is its whole source table, so neither the paragraph
  // pass nor the whole-slide fallback runs for it.
  exactOnly = false
) {
  // Find the PPTX container
  const container = document.querySelector(
    ".pptx-viewer .pptx-container"
  ) as HTMLElement;
  if (!container) {
    console.warn("No PPTX container found");
    return false;
  }

  // Clear any existing highlights
  clearPptxHighlights(container);

  // Focus on the specific slide if slideNumber is provided
  let searchContainer = container;
  if (slideNumber) {
    const slideElement = container.querySelector(
      `.pptx-slide-${slideNumber}`
    ) as HTMLElement;
    if (slideElement) {
      searchContainer = slideElement;
    } else {
      console.warn(
        `Slide ${slideNumber} not found, searching entire container`
      );
    }
  }

  // Create textmap of the search area
  const textmap = unveilPptxTextmap(searchContainer);

  // Handle snippet highlighting (numerical citations)
  if (snippet && snippet.trim()) {
    // Try to find the snippet directly in the textmap content
    const snippetRegex = contextToRegex(snippet);
    const snippetMatches = snippetRegex.exec(textmap.content);

    if (snippetMatches) {
      const snippetStartIndex = snippetMatches.index;
      const snippetEndIndex = snippetStartIndex + snippetMatches[0].length;
      const snippetElements = textmap.elementsBetween(
        snippetStartIndex,
        snippetEndIndex
      );

      let marks = [];
      for (const element of snippetElements) {
        const mark = highlightPartialPptxTextNode(element, snippet);
        if (mark) {
          marks.push(mark);
        }
      }

      if (marks.length > 0) {
        // Scroll to the first highlighted element
        if (marks[0]) {
          scrollToHighlight(marks[0]);
        }
        return true;
      }
    }
  }

  if (exactOnly) return false;

  // Fallback: try paragraph matching (fuzzy search)
  const regex = contextToRegex(paragraph);
  const matches = regex.exec(textmap.content);

  if (!matches) {
    // Fallback: highlight all text on the slide
    const allTextNodes = [];
    const walker = document.createTreeWalker(
      searchContainer,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: function (node) {
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;

          const tagName = parent.tagName.toLowerCase();
          if (["script", "style", "noscript"].includes(tagName)) {
            return NodeFilter.FILTER_REJECT;
          }

          // Skip empty or whitespace-only text nodes
          const text = node.textContent || "";
          if (text.trim().length === 0) {
            return NodeFilter.FILTER_REJECT;
          }

          return NodeFilter.FILTER_ACCEPT;
        },
      }
    );

    let node;
    while ((node = walker.nextNode())) {
      allTextNodes.push(node);
    }

    let highlightedCount = 0;
    let firstHighlightElement = null;

    for (const textNode of allTextNodes) {
      triggerHighlight(textNode);

      // Find the mark element that was just created for scrolling
      if (!firstHighlightElement) {
        const parent = textNode.parentElement;
        if (parent) {
          const markElement = parent.querySelector(
            ".fp-dark-highlight:last-child"
          );
          if (markElement) {
            firstHighlightElement = markElement;
          }
        }
      }
      highlightedCount++;
    }

    // Scroll to the first highlighted element
    if (firstHighlightElement) {
      scrollToHighlight(firstHighlightElement);
    }
    return highlightedCount > 0;
  }

  const startIndex = matches.index;
  const endIndex = startIndex + matches[0].length;
  const elements = textmap.elementsBetween(startIndex, endIndex);

  // Highlight the matched text elements
  let highlightedCount = 0;
  let firstHighlightElement = null;

  for (const element of elements) {
    // Only highlight actual text nodes, never background or container elements
    if (element.nodeType === Node.TEXT_NODE) {
      const textContent = element.textContent?.trim();
      if (textContent) {
        // Use triggerHighlight and then find the created mark element
        triggerHighlight(element);

        // Find the mark element that was just created
        if (!firstHighlightElement) {
          const parent = element.parentElement;
          if (parent) {
            const markElement = parent.querySelector(
              ".fp-dark-highlight:last-child"
            );
            if (markElement) {
              firstHighlightElement = markElement;
            }
          }
        }
        highlightedCount++;
      }
    }
  }

  // Scroll to the first highlighted element
  if (firstHighlightElement) {
    scrollToHighlight(firstHighlightElement);
  }

  return highlightedCount > 0;
}

/**
 * Scrolls to the specified slide in a PPTX viewer
 * @param container The PPTX container element
 * @param slideNumber The slide number to scroll to (1-indexed)
 */
export function scrollToSlide(container: HTMLElement, slideNumber: number) {
  const slideElement = container.querySelector(
    `.pptx-slide-${slideNumber}`
  ) as HTMLElement;

  if (slideElement) {
    // Check if the slide has transforms applied (which can interfere with scrollIntoView)
    const hasTransform =
      slideElement.style.transform && slideElement.style.transform !== "none";

    if (hasTransform) {
      scrollToTransformedElement(slideElement, container);
    } else {
      slideElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    return true;
  } else {
    console.warn(`Slide ${slideNumber} not found`);
    return false;
  }
}

/**
 * Manually scrolls to a transformed element by calculating the correct position
 */
function scrollToTransformedElement(
  element: HTMLElement,
  container: HTMLElement
) {
  try {
    // Get the element's position relative to the document
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Find the scrollable parent (the pptx-viewer container)
    const scrollableParent = container.closest(".pptx-viewer") as HTMLElement;
    if (!scrollableParent) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const scrollableRect = scrollableParent.getBoundingClientRect();

    // Calculate the element's position relative to the scrollable container
    const elementTop =
      elementRect.top - scrollableRect.top + scrollableParent.scrollTop;
    const elementHeight = elementRect.height;
    const viewportHeight = scrollableParent.clientHeight;

    // Center the slide in the viewport
    const slideCenter = elementTop + elementHeight / 2;
    const viewportCenter = viewportHeight / 2;
    const targetScrollTop = slideCenter - viewportCenter;

    // Smooth scroll to the calculated position
    scrollableParent.scrollTo({
      top: Math.max(0, targetScrollTop), // Ensure we don't scroll to negative position
      behavior: "smooth",
    });
  } catch (error) {
    console.warn("⚠️ Manual scroll error, using fallback:", error);
    element.scrollIntoView({ behavior: "smooth", block: "center" });
  }
}

/**
 * Scrolls to a highlighted element, taking into account CSS transforms and scaling
 */
function scrollToHighlight(element: Node) {
  const targetElement =
    element.nodeType === Node.TEXT_NODE
      ? element.parentElement
      : (element as HTMLElement);

  if (!targetElement) return;

  // Check if the element is inside a scaled/transformed slide
  const slideContainer = targetElement.closest(
    '[class*="pptx-slide-"], [class*="slide"]'
  ) as HTMLElement;
  if (slideContainer) {
    // Check if the slide has transforms applied
    const hasTransform =
      slideContainer.style.transform &&
      slideContainer.style.transform !== "none";

    if (hasTransform) {
      // Find the container for the manual scroll calculation
      const container = slideContainer.closest(
        ".pptx-container"
      ) as HTMLElement;
      if (container) {
        scrollToTransformedElement(slideContainer, container);

        // Then scroll to the specific element once the slide scroll above
        // (a manual `.scrollTo()` on the `.pptx-viewer` scroll host) settles.
        afterScrollSettles(() => {
          targetElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        });
      } else {
        slideContainer.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    } else {
      // First scroll the slide into view, then the specific element once
      // that scroll settles.
      slideContainer.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      afterScrollSettles(() => {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
    }
  } else {
    // Direct scroll to element
    targetElement.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }
}

/**
 * Highlights only a specific part of a text node by splitting it
 * Enhanced for PPTX content
 */
function highlightPartialPptxTextNode(textNode: Node, textToHighlight: string) {
  if (textNode.nodeType !== Node.TEXT_NODE) {
    // If it's not a text node, use the original method
    triggerHighlight(textNode);
    return;
  }

  const parent = textNode.parentNode;
  if (!parent) return;

  const content = textNode.textContent || "";
  const regex = contextToRegex(textToHighlight);
  const match = regex.exec(content);

  if (!match) {
    return;
  }

  const startPos = match.index;
  const endPos = startPos + match[0].length;

  const beforeText = content.substring(0, startPos);
  const highlightText = content.substring(startPos, endPos);
  const afterText = content.substring(endPos);

  const beforeNode = beforeText ? document.createTextNode(beforeText) : null;
  const highlightNode = document.createTextNode(highlightText);
  const afterNode = afterText ? document.createTextNode(afterText) : null;

  // Create the highlight element with PPTX-specific styling
  const mark = document.createElement("mark");
  mark.classList.add("fp-dark-highlight", "pptx-highlight");
  mark.appendChild(highlightNode);

  // Replace the original text node
  if (beforeNode) {
    parent.insertBefore(beforeNode, textNode);
  }

  parent.insertBefore(mark, textNode);

  if (afterNode) {
    parent.insertBefore(afterNode, textNode);
  }

  parent.removeChild(textNode);

  return mark;
}

/**
 * Turn a free-form context string into a RegExp that tolerates
 * any whitespace (space, newline, tab) by collapsing each run to \s+.
 */
function contextToRegex(context: string): RegExp {
  const trimmed = context.trim();
  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = escaped.replace(/\s+/g, "\\s*");
  return new RegExp(pattern, "gi");
}

/**
 * Clear all PPTX highlights from the container
 */
function clearPptxHighlights(container: HTMLElement) {
  let mark: Element | null = null;
  while (
    (mark = container.querySelector(".fp-dark-highlight, .pptx-highlight"))
  ) {
    const parent = mark.parentNode;
    if (!parent) return;

    // Move each child of the <mark> back into the DOM at the mark's position
    while (mark.firstChild) {
      parent.insertBefore(mark.firstChild, mark);
    }

    // Remove the now-empty <mark> element
    parent.removeChild(mark);
  }
}

/**
 * Creates a textmap for PPTX content, handling the specific DOM structure of slides
 */
function unveilPptxTextmap(root: HTMLElement) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: function (node) {
      // Skip nodes that are likely not content (e.g., in style tags, scripts, etc.)
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;

      const tagName = parent.tagName.toLowerCase();
      if (["script", "style", "noscript"].includes(tagName)) {
        return NodeFilter.FILTER_REJECT;
      }

      // Skip empty or whitespace-only text nodes
      const text = node.textContent || "";
      if (text.trim().length === 0) {
        return NodeFilter.FILTER_REJECT;
      }

      return NodeFilter.FILTER_ACCEPT;
    },
  });

  let content = "";
  const map: IndexToElement[] = [];
  let node: Node | null;

  while ((node = walker.nextNode())) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || "";
      map.push({
        index: content.length,
        node: node,
      });
      content += text;
    }
  }

  return new PptxTextmap(content, map);
}

/**
 * Creates a textmap from a specific set of nodes
 */
function makePptxTextmap(nodes: Node[]) {
  let content = "";
  const map: IndexToElement[] = [];

  for (const node of nodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || "";
      map.push({
        index: content.length,
        node: node,
      });
      content += text;
    }
  }

  return new PptxTextmap(content, map);
}

interface IndexToElement {
  index: number;
  node: Node;
}

/**
 * Textmap class for managing text content and its DOM node mapping in PPTX slides
 */
class PptxTextmap {
  constructor(
    public readonly content: string,
    private readonly map: IndexToElement[]
  ) {}

  elementAtIndex(index: number) {
    // Find the text node that contains the character at the given index
    for (let i = this.map.length - 1; i >= 0; i--) {
      if (this.map[i].index <= index) {
        return this.map[i].node;
      }
    }
    return null;
  }

  elementsBetween(start: number, stop: number) {
    const nodes = new Set<Node>();
    for (let i = start; i < stop; i++) {
      const node = this.elementAtIndex(i);
      if (node) {
        nodes.add(node);
      }
    }
    return Array.from(nodes);
  }
}
